import { useCallback } from "react";

import { useAppDispatch, useAppSelector } from "@lib/store";
import { actions } from "@lib/store/src/data/timeslots/actions";
import { isOfficeWorkdayDraft, Workday, WorkspaceReservation } from "@lib/store/src/data/timeslots/apiTypes";
import { getBuildingIdsThatRequireWorkspaceBooking } from "@lib/store/src/data/timeslots/selectors/getBuildingSections";
import { getConfirmedWorkspaceReservationsForWorkday } from "@lib/store/src/data/timeslots/selectors";
import { isOfficeWorkday } from "@lib/store/src/data/timeslots/utils";

interface Args {
  workday: Workday;
  workspaceReservation?: WorkspaceReservation;
}

export const useCleanupWorkday = ({ workday, workspaceReservation }: Args) => {
  const dispatch = useAppDispatch();

  const buildingIdsWithRequiredWorkspaceBooking = useAppSelector(getBuildingIdsThatRequireWorkspaceBooking);
  const workdayRequiresWorkspaceBooking = isOfficeWorkday(workday)
    ? buildingIdsWithRequiredWorkspaceBooking.has(workday.buildingId)
    : false;
  const currentWorkdayConfirmedWorkspaceReservations = useAppSelector(state =>
    getConfirmedWorkspaceReservationsForWorkday(state, workday?.id ?? null),
  );

  const cleanUpWorkdayIfRequired = useCallback(() => {
    if (isOfficeWorkdayDraft(workday)) return;

    // This is a bandaid that postpones our customers having to reconcile
    // with the fact that having both timeslot booking _and_ mandatory workspace
    // booking does not make much sense.
    if (
      !workspaceReservation &&
      workdayRequiresWorkspaceBooking &&
      currentWorkdayConfirmedWorkspaceReservations.length === 0
    ) {
      dispatch(actions.cancelWorkday(workday));
    }
  }, [
    dispatch,
    workday,
    workspaceReservation,
    workdayRequiresWorkspaceBooking,
    currentWorkdayConfirmedWorkspaceReservations,
  ]);

  return { cleanUpWorkdayIfRequired };
};
